// Copyright (c) Microsoft. All rights reserved.

import * as signalR from '@microsoft/signalr';
import { Constants } from '../../../Constants';
import { COPY } from '../../../assets/strings';
import { getFriendlyChatName } from '../../../libs/hooks/useChat';
import { AlertType } from '../../../libs/models/AlertType';
import { AuthorRoles, ChatMessageType, IChatMessage } from '../../../libs/models/ChatMessage';
import { IChatUser } from '../../../libs/models/ChatUser';
import { PlanState } from '../../../libs/models/Plan';
import { BackendServiceUrl } from '../../../libs/services/BaseService';
import { StoreMiddlewareAPI } from '../../app/store';
import { addAlert, setMaintenance } from '../app/appSlice';

import { UpdatePluginStatePayload } from '../conversations/ConversationsState';

/*
 * This is a module that encapsulates the SignalR connection
 * to the messageRelayHub on the server.
 */

// These have to match the callback names used in the backend
const enum SignalRCallbackMethods {
    ReceiveMessage = 'ReceiveMessage',
    ReceiveMessageUpdate = 'ReceiveMessageUpdate',
    UserJoined = 'UserJoined',
    ReceiveUserTypingState = 'ReceiveUserTypingState',
    ReceiveBotResponseStatus = 'ReceiveBotResponseStatus',
    GlobalDocumentUploaded = 'GlobalDocumentUploaded',
    ChatEdited = 'ChatEdited',
    ChatDeleted = 'ChatDeleted',
    GlobalSiteMaintenance = 'GlobalSiteMaintenance',
    PluginStateChanged = 'PluginStateChanged',
}

// Set up a SignalR connection to the messageRelayHub on the server
const setupSignalRConnectionToChatHub = () => {
    const connectionHubUrl = new URL('/messageRelayHub', BackendServiceUrl);
    const signalRConnectionOptions = {
        // Note: skipNegotiation must be false when using Azure SignalR Service
        // Azure SignalR requires negotiation to obtain connection information and access tokens
        logger: signalR.LogLevel.Warning,
    };

    // Create the connection instance
    // withAutomaticReconnect will automatically try to reconnect and generate a new socket connection if needed
    // Using custom retry delays for more robust reconnection strategy
    const hubConnection = new signalR.HubConnectionBuilder()
        .withUrl(connectionHubUrl.toString(), signalRConnectionOptions)
        .withAutomaticReconnect([0, 2000, 5000, 10000, 15000, 30000, 60000, 120000])
        .withHubProtocol(new signalR.JsonHubProtocol())
        .configureLogging(signalR.LogLevel.Information)
        .build();

    // Note: to keep the connection open the serverTimeout should be
    // larger than the KeepAlive value that is set on the server
    // For Azure SignalR, use longer timeouts to handle network latency and keep connections alive
    // keepAliveIntervalInMilliseconds: how often to send ping to server (default: 15s)
    // serverTimeoutInMilliseconds: how long to wait for server response before considering disconnected (default: 30s)
    // We use generous values to prevent premature disconnections for users with slower connections
    hubConnection.serverTimeoutInMilliseconds = 60000; // 60 seconds (reduced from 120s for faster failure detection)
    hubConnection.keepAliveIntervalInMilliseconds = 15000; // 15 seconds (standard)

    return hubConnection;
};

const registerCommonSignalConnectionEvents = (hubConnection: signalR.HubConnection, store: StoreMiddlewareAPI) => {
    // Re-establish the connection if connection dropped
    hubConnection.onclose((error) => {
        if (hubConnection.state === signalR.HubConnectionState.Disconnected) {
            const errorMessage = 'Connection closed due to error. Try refreshing this page to restart the connection';
            store.dispatch(
                addAlert({
                    message: String(errorMessage),
                    type: AlertType.Error,
                    id: Constants.app.CONNECTION_ALERT_ID,
                }),
            );
            console.log(errorMessage, error);
        }
    });

    hubConnection.onreconnecting((error) => {
        if (hubConnection.state === signalR.HubConnectionState.Reconnecting) {
            const errorMessage = 'Connection lost due to error. Reconnecting...';
            store.dispatch(
                addAlert({
                    message: String(errorMessage),
                    type: AlertType.Info,
                    id: Constants.app.CONNECTION_ALERT_ID,
                }),
            );
            console.log(errorMessage, error);
        }
    });

    hubConnection.onreconnected((connectionId = '') => {
        if (hubConnection.state === signalR.HubConnectionState.Connected) {
            const message = 'Connection reestablished. Resubscribing to conversations...';
            store.dispatch(addAlert({ message, type: AlertType.Info, id: Constants.app.CONNECTION_ALERT_ID }));
            console.log(message + ` Connected with connectionId ${connectionId}`);

            // Resubscribe to all conversation groups to resync missed events
            // Use void operator to explicitly ignore the promise return
            void (async () => {
                try {
                    const conversations = store.getState().conversations.conversations;
                    const chatIds = Object.keys(conversations);
                    
                    if (chatIds.length > 0) {
                        console.log(`Resubscribing to ${chatIds.length} conversation groups...`);
                        await Promise.all(
                            chatIds.map(async (chatId) => {
                                await hubConnection.invoke('AddClientToGroupAsync', chatId);
                            })
                        );
                        console.log('Successfully resubscribed to all conversation groups');
                        store.dispatch(
                            addAlert({
                                message: 'Reconnected and resubscribed to all conversations successfully.',
                                type: AlertType.Success,
                                id: Constants.app.CONNECTION_ALERT_ID,
                            })
                        );
                    } else {
                        store.dispatch(
                            addAlert({
                                message: 'Connection reestablished successfully.',
                                type: AlertType.Success,
                                id: Constants.app.CONNECTION_ALERT_ID,
                            })
                        );
                    }
                } catch (error) {
                    console.error('Error resubscribing to conversation groups:', error);
                    store.dispatch(
                        addAlert({
                            message: 'Reconnected but failed to resubscribe to some conversations. Please refresh the page.',
                            type: AlertType.Warning,
                            id: Constants.app.CONNECTION_ALERT_ID,
                        })
                    );
                }
            })();
        }
    });
};

const startSignalRConnection = (hubConnection: signalR.HubConnection, store: StoreMiddlewareAPI) => {
    registerCommonSignalConnectionEvents(hubConnection, store);
    
    // Show connecting status
    store.dispatch(
        addAlert({
            message: 'Connecting to chat service...',
            type: AlertType.Info,
            id: Constants.app.CONNECTION_ALERT_ID,
        })
    );
    
    hubConnection
        .start()
        .then(() => {
            console.assert(hubConnection.state === signalR.HubConnectionState.Connected);
            console.log('SignalR connection established');
            
            // Clear the connecting message on successful connection
            store.dispatch(
                addAlert({
                    message: 'Connected to chat service',
                    type: AlertType.Success,
                    id: Constants.app.CONNECTION_ALERT_ID,
                })
            );
            
            // Auto-dismiss success message after 2 seconds
            setTimeout(() => {
                const alerts = store.getState().app.alerts;
                const connectionAlert = alerts.find((alert) => alert.id === Constants.app.CONNECTION_ALERT_ID);
                if (connectionAlert && connectionAlert.type === AlertType.Success) {
                    // Alert will be removed by subsequent updates
                }
            }, 2000);
        })
        .catch((err) => {
            console.assert(hubConnection.state === signalR.HubConnectionState.Disconnected);
            console.error('SignalR Connection Error: ', err);
            
            // Provide user-friendly error message
            store.dispatch(
                addAlert({
                    message: 'Failed to connect to chat service. Retrying in 5 seconds...',
                    type: AlertType.Warning,
                    id: Constants.app.CONNECTION_ALERT_ID,
                })
            );
            
            setTimeout(() => {
                startSignalRConnection(hubConnection, store);
            }, 5000);
        });
};

const registerSignalREvents = (hubConnection: signalR.HubConnection, store: StoreMiddlewareAPI) => {
    hubConnection.on(
        SignalRCallbackMethods.ReceiveMessage,
        (chatId: string, senderId: string, message: IChatMessage) => {
            if (message.authorRole === AuthorRoles.Bot) {
                const loggedInUserId = store.getState().app.activeUserInfo?.id;
                const responseToLoggedInUser = loggedInUserId === senderId;
                message.planState =
                    message.type === ChatMessageType.Plan && responseToLoggedInUser
                        ? PlanState.PlanApprovalRequired
                        : PlanState.Disabled;
            }

            store.dispatch({ type: 'conversations/addMessageToConversationFromServer', payload: { chatId, message } });
        },
    );

    hubConnection.on(SignalRCallbackMethods.ReceiveMessageUpdate, (message: IChatMessage) => {
        const { chatId, id: messageId, content } = message;
        // If tokenUsage is defined, that means full message content has already been streamed and updated from server. No need to update content again.
        store.dispatch({
            type: 'conversations/updateMessageProperty',
            payload: {
                chatId,
                messageIdOrIndex: messageId,
                property: message.tokenUsage ? 'tokenUsage' : 'content',
                value: message.tokenUsage ?? content,
                frontLoad: true,
            },
        });
    });

    hubConnection.on(SignalRCallbackMethods.UserJoined, (chatId: string, userId: string) => {
        const user: IChatUser = {
            id: userId,
            online: false,
            fullName: '',
            emailAddress: '',
            isTyping: false,
            photo: '',
        };
        store.dispatch({ type: 'conversations/addUserToConversation', payload: { user, chatId } });
    });

    hubConnection.on(
        SignalRCallbackMethods.ReceiveUserTypingState,
        (chatId: string, userId: string, isTyping: boolean) => {
            store.dispatch({
                type: 'conversations/updateUserIsTypingFromServer',
                payload: { chatId, userId, isTyping },
            });
        },
    );

    hubConnection.on(SignalRCallbackMethods.ReceiveBotResponseStatus, (chatId: string, status: string | null) => {
        store.dispatch({
            type: 'conversations/updateBotResponseStatus',
            payload: { chatId, status: status ?? undefined },
        });
        console.log(`Bot response status for chat ${chatId} updated to: ${status}`);
    });

    hubConnection.on(SignalRCallbackMethods.GlobalDocumentUploaded, (fileNames: string, userName: string) => {
        store.dispatch(addAlert({ message: `${userName} uploaded ${fileNames} to all chats`, type: AlertType.Info }));
    });

    hubConnection.on(SignalRCallbackMethods.ChatEdited, (chat: { id: string; title?: string; threadId?: string }) => {
        const { id, title, threadId } = chat;
        if (!(id in store.getState().conversations.conversations)) {
            store.dispatch(
                addAlert({
                    message: `Chat ${id} not found in store. Chat edited signal from server is not processed.`,
                    type: AlertType.Error,
                }),
            );
            return;
        }

        // Update title if provided
        if (title) {
            store.dispatch({ type: 'conversations/editConversationTitle', payload: { id, newTitle: title } });
        }

        // Update threadId if provided
        if (threadId) {
            store.dispatch({ type: 'conversations/editConversationThreadId', payload: { id, newThreadId: threadId } });
        }
    });

    // User Id is that of the user who initiated the deletion.
    hubConnection.on(SignalRCallbackMethods.ChatDeleted, (chatId: string, userId: string) => {
        const conversations = store.getState().conversations.conversations;
        if (!(chatId in conversations)) {
            store.dispatch({
                message: `Chat ${chatId} not found in store. ChatDeleted signal from server was not processed. ${COPY.REFRESH_APP_ADVISORY}`,
                type: AlertType.Error,
            });
        } else {
            const friendlyChatName = getFriendlyChatName(conversations[chatId]);
            const deletedByAnotherUser = userId !== store.getState().app.activeUserInfo?.id;

            store.dispatch(
                addAlert({
                    message: deletedByAnotherUser
                        ? COPY.CHAT_DELETED_MESSAGE(friendlyChatName)
                        : `Chat {${friendlyChatName}} deleted successfully.`,
                    type: AlertType.Warning,
                }),
            );

            if (deletedByAnotherUser)
                store.dispatch({
                    type: 'conversations/disableConversation',
                    payload: chatId,
                });
        }
    });

    hubConnection.on(SignalRCallbackMethods.GlobalSiteMaintenance, () => {
        store.dispatch(setMaintenance(true));
    });

    hubConnection.on(
        SignalRCallbackMethods.PluginStateChanged,
        (chatId: string, pluginName: string, pluginState: boolean) => {
            store.dispatch({
                type: 'conversations/updatePluginState',
                payload: { id: chatId, pluginName: pluginName, newState: pluginState } as UpdatePluginStatePayload,
            });
        },
    );
};

// This is a singleton instance of the SignalR connection
let hubConnection: signalR.HubConnection | undefined = undefined;

// This function will return the singleton instance of the SignalR connection
export const getOrCreateHubConnection = (store: StoreMiddlewareAPI) => {
    if (hubConnection === undefined) {
        hubConnection = setupSignalRConnectionToChatHub();

        // Start the signalR connection to make sure messages are
        // sent to all clients and received by all clients
        startSignalRConnection(hubConnection, store);
        registerSignalREvents(hubConnection, store);
    }
    return hubConnection;
};
